"use client";

// ThreadFig — the constellation thread language, composed instead of grown.
// The developer depiction (Figma 125:676) uses the brush the hero once did —
// our cell stamped along authored paths at random size and rotation under a
// soft white glow, with anchor cells at the joints.
//
// THE RENDERER IS A CANVAS, NOT SVG. The first cut drew every stamp as an SVG
// <use> under one Gaussian-blur filter and animated each one — hundreds of
// filtered nodes re-rasterising through the reveal, which is exactly the kind
// of spend that made the section glitch on arrival. Now the stamps raster ONCE
// into a single canvas — FLAT, no per-stamp shadow — and the halo is that same
// bitmap copied to a second layer with one CSS blur, so the browser rasterises
// the glow once for the whole drawing instead of once per mark. The reveal is
// ONE animated clip-path circle on those two layers: it opens out of the
// creature's seat when the wrapper carries [data-in] and CLOSES FAST when the
// attribute lifts, so the lines never linger behind the next section.
//
// Coordinates are normalized (0..1) against the figure's own box; anchors are
// DOM (a handful), revealed by CSS transitions keyed on the same [data-in].

import { useEffect, useMemo, useRef, useState } from "react";
import { CELL } from "../../../lib/brand/cell";

/* the measured Figma brush (see the hero's provenance note) */
const BRUSH = { gap: 6.2, jit: 0.55, band: 0.95, sMin: 1.3, sMax: 5.2, bias: 1.75 };
const CELL_W = 1071;
const CELL_CX = 535.5;
const CELL_CY = 415.5;

const mulberry32 = (a: number) => () => {
  a |= 0;
  a = (a + 0x6d2b79f5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

export interface FigPath {
  /** normalized polyline, 0..1 of the figure box. */
  pts: [number, number][];
  /** index of the path this one continues from (kept for authoring clarity). */
  after?: number;
}

export interface FigAnchor {
  x: number;
  y: number;
  /** cell width in px (the label reads it too). */
  s?: number;
  label?: string;
  labelPos?: "above" | "below" | "left" | "right";
  /** reveal position along the system, 0..1 — when this anchor arrives. */
  fd?: number;
}

export default function ThreadFig({
  paths,
  anchors = [],
  className,
  children,
}: {
  paths: FigPath[];
  anchors?: FigAnchor[];
  className?: string;
  children?: React.ReactNode;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const glowRef = useRef<HTMLCanvasElement | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const put = () => {
      const w = Math.round(el.clientWidth);
      const h = Math.round(el.clientHeight);
      if (w > 0 && h > 0) setDims((d) => (d && d.w === w && d.h === h ? d : { w, h }));
    };
    put();
    const ro = new ResizeObserver(put);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // the cell path in its native 1071×831 frame — scaled per stamp at draw time
  const cellPath = useMemo(() => (typeof Path2D !== "undefined" ? new Path2D(CELL.path) : null), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !dims || !cellPath) return;
    // 1.25 is plenty for a field of soft 1–5px blobs under a blur — 2x here was
    // four times the fill for a difference nobody can see
    const dpr = Math.min(1.25, window.devicePixelRatio || 1);
    canvas.width = Math.round(dims.w * dpr);
    canvas.height = Math.round(dims.h * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, dims.w, dims.h);
    const rng = mulberry32(((dims.w * 73856093) ^ (dims.h * 19349663)) >>> 0);
    // THE GLOW IS ONE PASS, NOT ONE PER STAMP. `shadowBlur` on a fill blurs
    // every single stamp separately — hundreds of Gaussian ops for a texture
    // that never changes, and the reason this section still hitched on arrival.
    // Now the stamps are drawn FLAT into the canvas and the halo comes from a
    // single CSS `filter: blur()` on a duplicate layer (see .t1-fig__cv--glow),
    // which the compositor rasterises once.
    ctx.fillStyle = "#fff";
    for (const p of paths) {
      const pts = p.pts.map(([x, y]) => [x * dims.w, y * dims.h] as [number, number]);
      for (let i = 1; i < pts.length; i++) {
        const [x1, y1] = pts[i - 1];
        const [x2, y2] = pts[i];
        const len = Math.hypot(x2 - x1, y2 - y1);
        if (len < 1) continue;
        const ux = (x2 - x1) / len;
        const uy = (y2 - y1) / len;
        let dist = BRUSH.gap * rng();
        while (dist < len) {
          const size = BRUSH.sMin + Math.pow(rng(), BRUSH.bias) * (BRUSH.sMax - BRUSH.sMin);
          const off = (rng() - 0.5) * 2 * BRUSH.band;
          const sx = x1 + ux * dist - uy * off;
          const sy = y1 + uy * dist + ux * off;
          // setTransform beats save/translate/rotate/scale/restore: one matrix
          // write per stamp instead of five state ops and a stack push
          const k = (size / CELL_W) * dpr;
          const a = rng() * Math.PI * 2;
          const cos = Math.cos(a) * k;
          const sin = Math.sin(a) * k;
          ctx.setTransform(cos, sin, -sin, cos, sx * dpr, sy * dpr);
          ctx.translate(-CELL_CX, -CELL_CY);
          ctx.globalAlpha = 0.5 + rng() * 0.5;
          ctx.fill(cellPath);
          dist += BRUSH.gap * (1 - BRUSH.jit + rng() * BRUSH.jit * 2);
        }
      }
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    // the halo layer is the same bitmap, blurred by CSS — one drawImage
    const glow = glowRef.current;
    if (glow) {
      glow.width = canvas.width;
      glow.height = canvas.height;
      glow.getContext("2d")?.drawImage(canvas, 0, 0);
    }
  }, [dims, paths, cellPath]);

  return (
    <div ref={boxRef} className={`t1-fig${className ? " " + className : ""}`} aria-hidden>
      {/* the marks, then the SAME canvas bitmap blurred once beneath them as the
          halo — drawn by the browser off one texture, not by us per stamp */}
      <canvas ref={glowRef} className="t1-fig__cv t1-fig__cv--glow" style={dims ? { width: dims.w, height: dims.h } : undefined} />
      <canvas ref={canvasRef} className="t1-fig__cv" style={dims ? { width: dims.w, height: dims.h } : undefined} />
      {anchors.map((a, i) => (
        <span
          key={i}
          className="t1-node t1-fig__node"
          data-lab={a.labelPos ?? "above"}
          data-edge={a.x < 0.16 ? "l" : a.x > 0.8 ? "r" : undefined}
          style={{
            left: `${a.x * 100}%`,
            top: `${a.y * 100}%`,
            "--s": `${a.s ?? 34}px`,
            "--i": i,
            "--fd": (a.fd ?? 1).toFixed(3),
          } as React.CSSProperties}
        >
          <span className="t1-node__halo" />
          <span className="t1-node__c" />
          {a.label && <span className="t1-fig__lab">{a.label}</span>}
        </span>
      ))}
      {children}
    </div>
  );
}
